import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isAxiosError } from 'axios';
import type { Lender } from '../../api/lenders';
import { useAddLenderProducts } from '../../hooks/useLenders';
import { useProducts } from '../../hooks/useProducts';
import { buildProductTree, WIZARD_SERVICES } from './wizard/components/productTree';
import closeIcon from './assets/product-close.svg';
import badgeDot from './assets/product-badge-dot.svg';
import emptyIcon from './assets/add-product-empty.svg';
import searchIcon from './assets/add-product-search.svg';
import activeSearchIcon from './assets/add-product-search-active.svg';
import clearIcon from './assets/add-product-clear.svg';
import removeIcon from './assets/add-product-remove.svg';
import plusIcon from './assets/add-product-plus.svg';
import checkIcon from './assets/add-product-check.svg';
import chevronRight from './assets/add-product-chevron-right.svg';
import chevronDown from './assets/add-product-chevron-down.svg';

type ServiceFilter = 'All' | (typeof WIZARD_SERVICES)[number];
interface CatalogOption {
  urn: string;
  name: string;
  service: string;
  category: string;
  provider: string;
}

interface Props {
  lender: Lender;
  onClose: () => void;
  onAdded: () => void;
}

const focusStyle =
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand';

/** A draft selection; only Add Products writes to the lender catalog. */
export default function LenderProductAddDrawer({ lender, onClose, onAdded }: Props) {
  const catalog = useProducts();
  const addition = useAddLenderProducts();
  const [query, setQuery] = useState('');
  const [service, setService] = useState<ServiceFilter>('All');
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const titleId = useId();
  const basketId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const previousFocus = useRef<HTMLElement | null>(null);
  const submitting = useRef(false);

  const assigned = new Set(
    (lender.products ?? [])
      .filter((product) => typeof product === 'string' || product.active !== false)
      .map((product) => (typeof product === 'string' ? product : product.urn)),
  );
  const options = new Map<string, CatalogOption>();
  for (const group of buildProductTree(catalog.data ?? [])) {
    for (const category of group.productTypes) {
      for (const provider of category.providers) {
        for (const product of provider.products) {
          if (!assigned.has(product.urn))
            options.set(product.urn, {
              ...product,
              service: group.service,
              category: category.productType,
              provider: provider.provider,
            });
        }
      }
    }
  }
  const term = query.trim().toLowerCase();
  const results = [...options.values()].filter(
    (product) =>
      (service === 'All' || product.service === service) &&
      (!term ||
        [service === 'All' ? product.service : '', product.category, product.provider, product.name].some((value) =>
          value.toLowerCase().includes(term),
        )),
  );
  const draft = [...selected].flatMap((urn) => {
    const product = options.get(urn);
    return product ? [product] : [];
  });
  const canSelectAll = results.some((product) => !selected.has(product.urn));

  useEffect(() => {
    const overlay = overlayRef.current;
    const trigger = document.activeElement;
    if (trigger instanceof HTMLElement && !overlay?.contains(trigger)) previousFocus.current = trigger;
    searchRef.current?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const siblings = Array.from(document.body.children)
      .filter((element): element is HTMLElement => element instanceof HTMLElement && element !== overlay)
      .map((element) => ({ element, inert: element.inert }));
    siblings.forEach(({ element }) => {
      element.inert = true;
    });
    return () => {
      document.body.style.overflow = overflow;
      siblings.forEach(({ element, inert }) => {
        element.inert = inert;
      });
      queueMicrotask(() => {
        if (!overlay?.isConnected && previousFocus.current?.isConnected) previousFocus.current.focus();
      });
    };
  }, []);

  useEffect(() => {
    if (addition.isPending) dialogRef.current?.focus();
    else if (document.activeElement === dialogRef.current) searchRef.current?.focus();
  }, [addition.isPending]);

  const add = (products: CatalogOption[]) => {
    setSelected((current) => new Set([...current, ...products.map((product) => product.urn)]));
    setExpanded((current) => new Set([...current, ...products.map((product) => product.service)]));
    // Add becomes disabled, so keep keyboard focus inside the drawer.
    searchRef.current?.focus();
  };
  const dismiss = () => {
    if (!submitting.current) onClose();
  };
  const save = async () => {
    if (!draft.length || submitting.current || catalog.isError) return;
    submitting.current = true;
    try {
      await addition.mutateAsync({ urn: lender.urn, productUrns: draft.map((product) => product.urn) });
    } catch {
      // Keep the complete draft available for retry; the mutation owns the error state.
      return;
    } finally {
      submitting.current = false;
    }
    onAdded();
  };

  return createPortal(
    <div ref={overlayRef} className="fixed inset-x-0 bottom-0 top-[60px] z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/20" aria-hidden="true" />
      <section
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-busy={addition.isPending}
        tabIndex={-1}
        className="relative flex h-full w-full min-w-0 max-w-[668px] flex-col border border-[#ddd] bg-white text-[#273141] shadow-lg outline-none"
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            dismiss();
          }
          if (event.key !== 'Tab') return;
          const controls = Array.from(
            dialogRef.current?.querySelectorAll<HTMLElement>(
              'button:not(:disabled), input:not(:disabled), [tabindex="0"]',
            ) ?? [],
          );
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (!first) {
            event.preventDefault();
            dialogRef.current?.focus();
          } else if (
            event.shiftKey &&
            (document.activeElement === first || document.activeElement === dialogRef.current)
          ) {
            event.preventDefault();
            last.focus();
          } else if (
            !event.shiftKey &&
            (document.activeElement === last || document.activeElement === dialogRef.current)
          ) {
            event.preventDefault();
            first.focus();
          }
        }}
      >
        <header className="flex shrink-0 items-center justify-between px-5 pb-5 pt-[22px] sm:px-10">
          <h2 id={titleId} className="text-[21px] font-medium leading-8 text-black">
            Add Product
          </h2>
          <button
            type="button"
            onClick={dismiss}
            disabled={addition.isPending}
            aria-label="Close add product drawer"
            className={`flex h-8 w-8 items-center justify-center rounded disabled:opacity-40 ${focusStyle}`}
          >
            <img src={closeIcon} alt="" />
          </button>
        </header>
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 pb-5 sm:px-10">
          <section aria-labelledby={basketId} className="rounded-lg bg-[#f9f9fa] p-6 [font-family:Inter,sans-serif]">
            <div className="flex items-center justify-between gap-2">
              <h3 id={basketId} className="text-[15px] font-semibold leading-[18px]">
                Additional Products
              </h3>
              <p
                role="status"
                className={`text-xs font-semibold leading-[15px] ${draft.length ? 'text-[#5140e9]' : 'text-[#5e6572]'}`}
              >
                {draft.length
                  ? `${draft.length} ${draft.length === 1 ? 'product' : 'products'} selected`
                  : '0 selected'}
              </p>
            </div>
            {draft.length === 0 ? (
              <div className="flex min-h-[144px] flex-col items-center justify-center gap-3 pt-5 text-center">
                <img src={emptyIcon} alt="" />
                <p className="text-sm font-medium text-[#5e6572]">
                  No additional products added yet. Search and add items to your stack.
                </p>
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {WIZARD_SERVICES.map((name) => {
                  const products = draft.filter((product) => product.service === name);
                  if (!products.length) return null;
                  const panelId = `${basketId}-${name}`;
                  return (
                    <div key={name}>
                      <button
                        type="button"
                        aria-expanded={expanded.has(name)}
                        aria-controls={panelId}
                        disabled={addition.isPending}
                        onClick={() =>
                          setExpanded((current) => {
                            const next = new Set(current);
                            if (next.has(name)) next.delete(name);
                            else next.add(name);
                            return next;
                          })
                        }
                        className={`flex items-center gap-2 rounded text-xs font-medium uppercase text-[#5e6572] ${focusStyle}`}
                      >
                        <img src={expanded.has(name) ? chevronDown : chevronRight} alt="" />
                        {name} ({products.length})
                      </button>
                      <div id={panelId} hidden={!expanded.has(name)} className="mt-2 space-y-2">
                        {products.map((product) => (
                          <div
                            key={product.urn}
                            className="flex items-center gap-2 rounded-lg border border-[#ddd] bg-white px-3 py-2"
                          >
                            <img src={badgeDot} alt="" />
                            <div className="min-w-0 flex-1 space-y-0.5 [overflow-wrap:anywhere]">
                              <p className="text-[10px] uppercase leading-[12px] tracking-[0.5px] text-[#5e6572]/60">
                                {product.service} · {product.category}
                              </p>
                              <p className="text-[11px] font-semibold uppercase leading-[13px] tracking-[0.3px] text-[#5e6572]">
                                {product.provider}
                              </p>
                              <p className="text-sm font-medium leading-[17px] text-black">{product.name}</p>
                            </div>
                            <button
                              type="button"
                              disabled={addition.isPending}
                              aria-label={`Remove ${product.name} by ${product.provider} from selection`}
                              onClick={() => {
                                setSelected((current) => {
                                  const next = new Set(current);
                                  next.delete(product.urn);
                                  return next;
                                });
                                searchRef.current?.focus();
                              }}
                              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded ${focusStyle}`}
                            >
                              <img src={removeIcon} alt="" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>
          <div className="space-y-5 [font-family:Inter,sans-serif]">
            <label className="flex min-h-[41px] items-center gap-3 rounded-lg border border-[#ddd] bg-[#f9f9fa] px-3 py-2.5 focus-within:border-[#5140e9] focus-within:bg-white focus-within:ring-1 focus-within:ring-[#5140e9]">
              <img src={query ? activeSearchIcon : searchIcon} alt="" />
              <span className="sr-only">Search products</span>
              <input
                ref={searchRef}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                disabled={addition.isPending}
                placeholder="Search products..."
                className="min-w-0 flex-1 bg-transparent text-base leading-5 outline-none placeholder:text-[#5e6572] sm:text-sm sm:leading-[17px]"
              />
              {query && (
                <button
                  type="button"
                  disabled={addition.isPending}
                  aria-label="Clear product search"
                  onClick={() => {
                    setQuery('');
                    searchRef.current?.focus();
                  }}
                  className={`rounded ${focusStyle}`}
                >
                  <img src={clearIcon} alt="" />
                </button>
              )}
            </label>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div role="group" aria-label="Filter by service" className="flex flex-wrap gap-2">
                {(['All', ...WIZARD_SERVICES] as const).map((name) => (
                  <button
                    key={name}
                    type="button"
                    aria-pressed={service === name}
                    disabled={addition.isPending}
                    onClick={() => setService(name)}
                    className={`h-8 rounded-full border px-4 text-[13px] leading-4 ${focusStyle} ${
                      service === name
                        ? 'border-[#5140e9] bg-[#5140e9] font-semibold text-white'
                        : 'border-[#ddd] bg-white font-medium text-[#5e6572]'
                    }`}
                  >
                    {name}
                  </button>
                ))}
              </div>
              <button
                type="button"
                disabled={!canSelectAll || addition.isPending || catalog.isError}
                onClick={() => add(results)}
                className={`rounded px-2 text-xs text-[#6265e5] disabled:opacity-50 ${focusStyle}`}
              >
                Select All
              </button>
            </div>
            {catalog.isLoading ? (
              <p role="status" className="text-sm text-[#5e6572]">
                Loading products...
              </p>
            ) : catalog.isError ? (
              <div role="alert" className="text-sm text-red-700">
                Products could not be loaded.{' '}
                <button
                  type="button"
                  disabled={addition.isPending}
                  onClick={() => void catalog.refetch()}
                  className={`rounded underline ${focusStyle}`}
                >
                  Try again
                </button>
              </div>
            ) : results.length === 0 ? (
              <p role="status" className="text-sm text-[#5e6572]">
                No products found.
              </p>
            ) : results.length > 0 ? (
              <div className="overflow-hidden rounded-lg border border-[#ddd]">
                {results.map((product) => (
                  <div
                    key={product.urn}
                    className={`flex items-center justify-between gap-3 border-b border-[#ebeff5] px-4 py-3 last:border-b-0 ${
                      selected.has(product.urn) ? 'bg-[#f9f9fa]' : 'bg-white'
                    }`}
                  >
                    <div className="min-w-0 space-y-0.5 [overflow-wrap:anywhere]">
                      <p className="text-[10px] uppercase leading-[12px] tracking-[0.5px] text-[#5e6572]/60">
                        {product.service} · {product.category}
                      </p>
                      <p className="text-[11px] font-semibold uppercase leading-[13px] tracking-[0.3px] text-[#5e6572]">
                        {product.provider}
                      </p>
                      <p className="text-sm font-medium leading-[17px] text-black">{product.name}</p>
                    </div>
                    <button
                      type="button"
                      disabled={selected.has(product.urn) || addition.isPending}
                      onClick={() => add([product])}
                      aria-label={`${selected.has(product.urn) ? 'Added' : 'Add'} ${product.name} by ${
                        product.provider
                      }`}
                      className={`flex shrink-0 items-center gap-1 rounded-md border px-3 py-1.5 text-xs font-semibold leading-[15px] ${focusStyle} ${
                        selected.has(product.urn)
                          ? 'border-[#39b54a] bg-[#e8f8ee] text-[#39b54a]'
                          : 'border-[#5140e9] bg-white text-[#5140e9]'
                      }`}
                    >
                      <img src={selected.has(product.urn) ? checkIcon : plusIcon} alt="" />
                      {selected.has(product.urn) ? 'Added' : 'Add'}
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        <footer className="shrink-0 space-y-4 px-5 pb-6 pt-5 sm:px-10 sm:pb-14">
          {addition.isError && (
            <p role="alert" className="text-sm text-red-700">
              {isAxiosError(addition.error) && addition.error.response?.status === 409
                ? 'The lender catalog changed. Reload the page and try again.'
                : 'Products could not be added. Please try again.'}
            </p>
          )}
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={dismiss}
              disabled={addition.isPending}
              className={`h-11 rounded-lg border border-[#5140e9] bg-white px-6 text-[15px] font-semibold disabled:opacity-40 sm:w-[155px] ${focusStyle}`}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void save()}
              disabled={!draft.length || addition.isPending || catalog.isError || catalog.isLoading}
              className={`h-11 rounded-lg bg-[#5140e9] px-5 text-[15px] font-semibold text-white disabled:opacity-20 sm:w-[151px] ${focusStyle}`}
            >
              {addition.isPending ? 'Adding...' : 'Add Products'}
            </button>
          </div>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
